import { createContext, useContext, useLayoutEffect } from "react";
import type { Breadcrumb } from "./breadcrumb";
import type {
  PageAction,
  PageLeadingVisual,
  PageSubtitle,
  PageTab,
} from "./page-prop-types";

// Leaves call these hooks to register header props on the singleton `<Page>`
// up at the app shell. Array/object values must be memoized — they're
// `useLayoutEffect` deps.

interface PageHeaderSlotSetters {
  setTitle: (value: string | undefined) => void;
  setSubtitle: (value: PageSubtitle | undefined) => void;
  setBreadcrumbs: (value: Breadcrumb[] | undefined) => void;
  setLeadingVisual: (value: PageLeadingVisual | undefined) => void;
  setActions: (value: PageAction[] | undefined) => void;
  setTabs: (value: PageTab[] | undefined) => void;
  setHideTitle: (value: boolean) => void;
}

const NOOP_SETTERS: PageHeaderSlotSetters = {
  setTitle: () => {},
  setSubtitle: () => {},
  setBreadcrumbs: () => {},
  setLeadingVisual: () => {},
  setActions: () => {},
  setTabs: () => {},
  setHideTitle: () => {},
};

export const PageHeaderSlotContext =
  createContext<PageHeaderSlotSetters>(NOOP_SETTERS);

export function usePageTitle(title: string | undefined): void {
  const { setTitle } = useContext(PageHeaderSlotContext);
  useLayoutEffect(() => {
    setTitle(title);
    return () => setTitle(undefined);
  }, [title, setTitle]);
}

export function usePageSubtitle(subtitle: PageSubtitle | undefined): void {
  const { setSubtitle } = useContext(PageHeaderSlotContext);
  useLayoutEffect(() => {
    setSubtitle(subtitle);
    return () => setSubtitle(undefined);
  }, [subtitle, setSubtitle]);
}

export function usePageBreadcrumbs(
  breadcrumbs: Breadcrumb[] | undefined,
): void {
  const { setBreadcrumbs } = useContext(PageHeaderSlotContext);
  useLayoutEffect(() => {
    setBreadcrumbs(breadcrumbs);
    return () => setBreadcrumbs(undefined);
  }, [breadcrumbs, setBreadcrumbs]);
}

export function usePageLeadingVisual(
  leadingVisual: PageLeadingVisual | undefined,
): void {
  const { setLeadingVisual } = useContext(PageHeaderSlotContext);
  useLayoutEffect(() => {
    setLeadingVisual(leadingVisual);
    return () => setLeadingVisual(undefined);
  }, [leadingVisual, setLeadingVisual]);
}

export function usePageActions(actions: PageAction[] | undefined): void {
  const { setActions } = useContext(PageHeaderSlotContext);
  useLayoutEffect(() => {
    setActions(actions);
    return () => setActions(undefined);
  }, [actions, setActions]);
}

export function usePageTabs(tabs: PageTab[] | undefined): void {
  const { setTabs } = useContext(PageHeaderSlotContext);
  useLayoutEffect(() => {
    setTabs(tabs);
    return () => setTabs(undefined);
  }, [tabs, setTabs]);
}

export function useHidePageTitle(hide = true): void {
  const { setHideTitle } = useContext(PageHeaderSlotContext);
  useLayoutEffect(() => {
    setHideTitle(hide);
    return () => setHideTitle(false);
  }, [hide, setHideTitle]);
}

export interface PageHeaderConfig {
  title?: string;
  subtitle?: PageSubtitle;
  breadcrumbs?: Breadcrumb[];
  leadingVisual?: PageLeadingVisual;
  actions?: PageAction[];
  tabs?: PageTab[];
}

export function usePageHeader(config: PageHeaderConfig): void {
  usePageTitle(config.title);
  usePageSubtitle(config.subtitle);
  usePageBreadcrumbs(config.breadcrumbs);
  usePageLeadingVisual(config.leadingVisual);
  usePageActions(config.actions);
  usePageTabs(config.tabs);
}
